import type {CollectionsAnalyticsViewADto} from '@cohort/admin-schemas/digitalAssetCollection';
import DigitalAssetCollectionPerkBadge from '@cohort/merchants/components/digital-assets/DigitalAssetCollectionPerkBadge';
import DigitalAssetCollectionPerksList from '@cohort/merchants/components/digital-assets/DigitalAssetCollectionPerksList';
import {DigitalAssetCollectionStatusBadge} from '@cohort/merchants/components/digital-assets/DigitalAssetCollectionStatusBadge';
import {DigitalAssetVisual} from '@cohort/merchants/components/DigitalAssetVisual';
import {InformationTootlip} from '@cohort/merchants/components/InformationTooltip';
import {getDigitalAssetPerksRoute} from '@cohort/merchants/lib/Pages';
import type {CampaignRewardStepValues} from '@cohort/merchants/pages/campaigns/campaign/edit/CampaignRewardStep';
import {formatDate} from '@cohort/shared/utils/format';
import {ArrowSquareOut} from '@phosphor-icons/react';
import type {UseFormRegister} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';

type DigitalAssetCollectionsListProps = {
  digitalAssetCollections: Array<CollectionsAnalyticsViewADto>;
  register: UseFormRegister<CampaignRewardStepValues>;
};

type DigitalAssetCollectionsListItemProps = {
  digitalAssetCollection: CollectionsAnalyticsViewADto;
  register: DigitalAssetCollectionsListProps['register'];
};

const DigitalAssetCollectionsListItem: React.FC<DigitalAssetCollectionsListItemProps> = ({
  digitalAssetCollection,
  register,
}) => {
  const {t} = useTranslation('pages', {
    keyPrefix: 'campaigns.campaign.edit.reward.digitalAssetCollectionsList',
  });

  return (
    <li className="border-t-2 border-slate-100 py-4">
      <label className="flex w-full items-center gap-4">
        <input
          data-testid="digitalAssetCollectionId"
          type="radio"
          className="text-primary-500 form-radio h-4 w-4"
          value={digitalAssetCollection.id}
          {...register('digitalAssetCollectionId')}
        />
        <div className="grid flex-1 items-center gap-6 [grid-template-columns:1fr_min-content_300px]">
          <section className="flex items-center gap-4 truncate">
            <DigitalAssetVisual
              digitalAssetImageFileKey={digitalAssetCollection.imageFileKey}
              animationFileKey={digitalAssetCollection.animationFileKey}
              className="rounded-md"
              size={40}
            />
            <div className="flex flex-col gap-2">
              <div className="truncate">
                <h3 className="truncate text-sm font-medium">
                  {digitalAssetCollection.internalName}
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <DigitalAssetCollectionPerkBadge perksCount={digitalAssetCollection.perks.length} />
                {digitalAssetCollection.perks.length > 0 && (
                  <InformationTootlip
                    content={
                      <div className="p-2">
                        <DigitalAssetCollectionPerksList
                          perks={digitalAssetCollection.perks}
                          size="small"
                        />
                      </div>
                    }
                  />
                )}
              </div>
            </div>
          </section>
          <section>
            <DigitalAssetCollectionStatusBadge
              status={digitalAssetCollection.status}
              campaignsCount={digitalAssetCollection.campaignsCount}
              size="small"
            />
          </section>
          <section className="flex w-full items-center justify-end gap-8">
            <span data-testid="last-edit" className="text-xs text-slate-500">
              {t('labelLastEdit')}
              {formatDate(digitalAssetCollection.updatedAt)}
            </span>
            <Link
              to={getDigitalAssetPerksRoute(digitalAssetCollection.id).path}
              target="_blank"
              rel="noopener noreferrer"
            >
              <button type="button" className="rounded-lg border p-2">
                <ArrowSquareOut className="h-4 w-4 stroke-2 text-primary" />
              </button>
            </Link>
          </section>
        </div>
      </label>
    </li>
  );
};

const DigitalAssetCollectionsList: React.FC<DigitalAssetCollectionsListProps> = ({
  digitalAssetCollections,
  register,
}) => (
  <ul>
    {digitalAssetCollections.map(digitalAssetCollection => (
      <DigitalAssetCollectionsListItem
        key={digitalAssetCollection.id}
        digitalAssetCollection={digitalAssetCollection}
        register={register}
      />
    ))}
  </ul>
);
export default DigitalAssetCollectionsList;
